<template>
  <form-base :teleport-target="dialogTeleportTarget" hide-required-legend>
    <template #form>
      <v-form ref="form">
        <div class="d-flex flex-column gap-4">
          <text-field
            required
            type="text"
            v-model="localField.label"
            :rules="$validator.rules.required($t('customForms.labels.fieldLabel'))"
            @keydown.enter.stop
            data-testid="custom-fields-form-field-label-input"
            :label="$t('customForms.labels.fieldLabel')" />

          <custom-forms-type-selector
            required
            data-testid="custom-fields-form-field-type-select"
            v-model="localField.type"
            :rules="$validator.rules.required($t('customForms.labels.fieldType'))" />

          <text-field
            type="text"
            class="mt-10"
            data-testid="custom-fields-form-description-input"
            v-model="localField.description"
            :label="$t('forms.common.labels.description')"
            @keydown.enter.stop />

          <text-field
            v-if="!typesWithoutPlaceholder.includes(localField.type)"
            type="text"
            v-model="localField.placeholder"
            data-testid="custom-fields-form-example-value-input"
            :label="$t('customForms.labels.exampleValue')"
            @keydown.enter.stop />

          <template v-if="localField.type === novaCore.CustomFieldType.Action">
            <v-select
              multiple
              v-model="localField.extraFields.dropDownValues"
              :label="$t('customForms.labels.featuresChoices')"
              required
              data-testid="custom-fields-form-field-features-select"
              :items="Object.entries(novaCore.CustomFormsFeatures)"
              :item-title="key => novaCore.CustomFormsFeaturesData[key[1]].title"
              :item-value="key => key[1]" />
          </template>

          <v-alert type="info" color="grey" v-if="isDocField">
            <i18n-t
              keypath="customForms.messages.maximumInSizeOnlyOneImageTextZipOrSpreadsheetAllowedToUploadMultipleDocsZipFirstThenUpload">
              <template #size><strong>50MB</strong></template>
              <template #imageLimit>
                <strong>{{ $t('customForms.messages.oneImage') }}</strong>
              </template>
              <template #typeLimitText>
                <strong>{{ $t('global.basicTerms.text') }}</strong>
              </template>
              <template #typeLimitZip>
                <strong>{{ $t('global.basicTerms.zip') }}</strong>
              </template>
              <template #typeLimitSpreadsheet>
                <strong>{{ $t('global.basicTerms.spreadsheet') }}</strong>
              </template>
            </i18n-t>
          </v-alert>

          <v-alert type="info" color="grey" v-else-if="isMultiDocField">
            <p>
              <i18n-t
                keypath="customForms.messages.maximumInSizePerFileOnlyImageTextZipSpreadsheetAllowed">
                <template #size><strong>50MB</strong></template>
                <template #count>
                  <strong>10</strong>
                </template>
                <template #image>
                  <strong>{{ $t('global.basicTerms.image') }}</strong>
                </template>
                <template #text>
                  <strong>{{ $t('global.basicTerms.text') }}</strong>
                </template>
                <template #zip>
                  <strong>{{ $t('global.basicTerms.zip') }}</strong>
                </template>
                <template #spreadsheet>
                  <strong>{{ $t('global.basicTerms.spreadsheet') }}</strong>
                </template>
              </i18n-t>
            </p>
          </v-alert>

          <!-- TODO: Translate -->
          <custom-field-drop-down-choices-form
            v-if="hasDropDownValues"
            v-model="localField.extraFields.dropDownValues"></custom-field-drop-down-choices-form>

          <div
            class="d-flex flex-row justify-space-between gap-6"
            v-if="isNumberField || isTextField">
            <text-field
              type="number"
              v-model.number="localField.extraFields.minLengthOrValue"
              :rules="
                isTextField
                  ? $validator.rules.optionalNumberBetween(0, maxSizeTextField(localField.type))
                  : []
              "
              :label="
                isNumberField
                  ? $t('customForms.labels.minimumValue')
                  : $t('customForms.labels.minimumLength')
              "
              @keydown.enter.stop />

            <text-field
              type="number"
              v-model.number="localField.extraFields.maxLengthOrValue"
              :rules="
                isTextField
                  ? $validator.rules.optionalNumberBetween(0, maxSizeTextField(localField.type))
                  : []
              "
              :label="
                isNumberField
                  ? $t('customForms.labels.maxValue')
                  : $t('customForms.labels.maxLength')
              "
              @keydown.enter.stop />
          </div>
        </div>
      </v-form>
    </template>

    <template #form-actions>
      <action-group
        @cancel="$emit('close')"
        class="pr-0"
        confirm-icon="upload"
        :confirm-label="$t('global.buttons.save')"
        :cancel-label="$t('global.buttons.nevermind')"
        cancel-color="error"
        @confirm="submit"></action-group>
    </template>
  </form-base>
</template>

<script>
import { FIELDS_WITHOUT_PLACEHOLDER } from '@nova/core';
import { inject } from 'vue';

export default {
  props: {
    field: {
      type: Object,
      required: false,
      id: String
    },
    labelSuggestion: {
      type: String,
      required: false
    }
  },
  computed: {
    isDocField() {
      return this.localField.type === this.novaCore.CustomFieldType.Document;
    },
    isMultiDocField() {
      return this.localField.type === this.novaCore.CustomFieldType.MultiDocument;
    },
    isNumberField() {
      return this.localField.type === this.novaCore.CustomFieldType.Number;
    },
    isTextField() {
      return (
        this.localField.type === this.novaCore.CustomFieldType.String ||
        this.localField.type === this.novaCore.CustomFieldType.BigString
      );
    },
    hasDropDownValues() {
      return (
        this.localField.type &&
        (this.localField.type === this.novaCore.CustomFieldType.DropDown ||
          this.localField.type === this.novaCore.CustomFieldType.DropDownMultiSelect ||
          this.localField.type === this.novaCore.CustomFieldType.ComboBox)
      );
    }
  },
  data() {
    return {
      localField: {},
      typesWithoutPlaceholder: Object.freeze(FIELDS_WITHOUT_PLACEHOLDER)
    };
  },
  setup() {
    const dialogTeleportTarget = inject('dialogActionsRef');
    return { dialogTeleportTarget };
  },
  methods: {
    maxSizeTextField(fieldType) {
      if (fieldType === this.novaCore.CustomFieldType.String) {
        return 255;
      }

      if (fieldType === this.novaCore.CustomFieldType.BigString) {
        return 1000;
      }
    },
    async submit() {
      if (await this.$isFormValid(this.$refs.form)) {
        this.$emit('save', this.localField);
      }
    }
  },
  async mounted() {
    if (this.field?.id) {
      this.localField = { ...this.field };
    } else {
      this.localField = {
        label: this.labelSuggestion || null,
        description: null,
        type: null,
        placeholder: null
      };
    }

    if (!this.localField.extraFields) {
      this.localField.extraFields = {};
    }
  }
};
</script>
